import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import useResponsive from '@/hooks/useResponsive';

import type {
  OrderEventFF as OrderEventFFType,
  OrderEvent as OrderEventType,
} from '@/types/parcel/orders/orderById.interface';
import type { Address, Person } from '@/types/parcel/orders/orders.interface';
// import { useTranslation } from 'react-i18next';
import type { SvgIconTypeMap } from '@mui/material';
import type { OverridableComponent } from '@mui/material/OverridableComponent';

import LocationData from '../dependencies/locationData';
import OrderEventFF from './OrderEventFF';
import OrderEventsHistory from './OrderEventsHistory';

interface OrderDataAdditional {
  notes?: string;
}

interface OrderDataBase {
  origin?: Partial<Address>;
  destination?: Partial<Address>;
  sender?: Partial<Person>;
  recipient?: Partial<Person>;
  additional: OrderDataAdditional;
}

interface LocationDetailsProps {
  orderData?: OrderDataBase;
  orderEvents?: (OrderEventType | OrderEventFFType)[];
  isFFOrder?: boolean;
  orderId?: string;
  handleActivityIcon: (
    status: string,
  ) => OverridableComponent<SvgIconTypeMap<Record<string, never>, 'svg'>>;
}

const LocationDetails = ({
  orderData,
  orderEvents,
  isFFOrder,
  orderId,
  handleActivityIcon,
}: LocationDetailsProps) => {
  const isTablet = useResponsive('up', 'lg');
  const theme = useTheme();

  const deliveredEvent = orderEvents?.find((event) => 'status' in event && event.status === '4002');
  const hasDeliveredEvent = !!deliveredEvent;

  const eventHasCoordinates =
    deliveredEvent && 'lat' in deliveredEvent && 'lng' in deliveredEvent
      ? !!(deliveredEvent.lat && deliveredEvent.lng)
      : false;

  const destinationLat =
    eventHasCoordinates && deliveredEvent && 'lat' in deliveredEvent ? deliveredEvent.lat : null;
  const destinationLng =
    eventHasCoordinates && deliveredEvent && 'lng' in deliveredEvent ? deliveredEvent.lng : null;
  const hasCoordinates = !!(destinationLat && destinationLng);

  const handleOpenGoogleMaps = () => {
    if (hasCoordinates) {
      window.open(`https://maps.google.com/?q=${destinationLat},${destinationLng}`, '_blank');
    }
  };

  const handleCopyCoordinates = () => {
    if (hasCoordinates) {
      navigator.clipboard.writeText(`${destinationLat}, ${destinationLng}`);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        paddingRight: '1rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isTablet ? 'row' : 'column',
          gap: '1.5rem',
          width: '100%',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <LocationData
            Title="Origen"
            data={{
              address: orderData?.origin,
              person: orderData?.sender,
              notes: '',
            }}
            icon={<PlaceOutlinedIcon sx={{ width: '25px', height: 'auto' }} />}
          />
        </Box>
        <Box sx={{ width: '100%' }}>
          <LocationData
            Title="Destino"
            data={{
              address: orderData?.destination,
              person: orderData?.recipient,
              notes: '',
            }}
            icon={<RouteOutlinedIcon sx={{ width: '25px', height: 'auto' }} />}
            actionButton={
              hasDeliveredEvent ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip
                    title={
                      hasCoordinates ? 'Ver ubicación en Google Maps' : 'Coordenadas no disponibles'
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        disabled={!hasCoordinates}
                        onClick={handleOpenGoogleMaps}
                        startIcon={<LocationOnIcon />}
                        sx={{
                          backgroundColor: hasCoordinates ? '#85C440' : undefined,
                          color: hasCoordinates ? '#FFFFFF' : undefined,
                          whiteSpace: 'nowrap',
                          fontWeight: 600,
                          borderRadius: '28px',
                          '&:hover': hasCoordinates
                            ? {
                                backgroundColor: '#6FA836',
                              }
                            : undefined,
                        }}
                      >
                        Ver en Google Maps
                      </Button>
                    </span>
                  </Tooltip>
                  {hasCoordinates && (
                    <Tooltip title="Copiar coordenadas">
                      <IconButton
                        onClick={handleCopyCoordinates}
                        sx={{
                          color: '#85C440',
                          '&:hover': {
                            backgroundColor: 'rgba(133, 196, 64, 0.1)',
                          },
                        }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ) : undefined
            }
          />
        </Box>
      </Box>

      <Divider sx={{ width: '100%', my: 0.5 }} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: isTablet ? 'row' : 'column',
          gap: '1.5rem',
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: isTablet ? '50%' : '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: theme.palette.secondary.main }}>
            Notas:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              margin: '1rem 0',
              color: theme.palette.grey[800],
              textAlign: 'justify',
            }}
          >
            {orderData?.additional?.notes ?? ''}
          </Typography>
        </Box>

        <Box
          sx={{
            width: isTablet ? '50%' : '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {Array.isArray(orderEvents) &&
              (isFFOrder ? (
                orderEvents.map((event, i) => {
                  if ('name' in event && 'sku' in event && 'quantity' in event) {
                    return <OrderEventFF key={i} {...(event as OrderEventFFType)} />;
                  }
                  return null;
                })
              ) : (
                <OrderEventsHistory
                  key={orderId}
                  events={orderEvents.filter(
                    (event): event is OrderEventType =>
                      'status' in event && 'statusNameES' in event,
                  )}
                  handleActivityIcon={handleActivityIcon}
                />
              ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LocationDetails;
